import React from 'react';
import { Button, Alert } from 'reactstrap';
import numeral from 'numeral';

function billingFrequencyLabel(frequency) {
  switch (frequency) {
    case 'month': return '/Mo';
    case 'year': return '/Yr';
    default: return '';
  }
}

class Upgrade extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'selection',
      selectedOption: this.getDefaultOption(props),
      couponCode: '',
      couponApplying: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.state.couponApplying && this.props.couponResponse !== prevProps.couponResponse) {
      this.setState({ couponApplying: false });
    }

    if (this.state.step === 'processing') {
      if (this.props.upgradeResult && !prevProps.upgradeResult) {
        this.setState({ step: 'success' });
      }
      if (this.props.upgradeError && !prevProps.upgradeError) {
        this.setState({ step: 'confirmation' });
      }
    }
  }

  getDefaultOption(props) {
    const { membershipOptions, membership } = props;
    const currentProductName = membership && membership.productName;
    const options = membershipOptions || [];
    if (currentProductName === 'Monthly') {
      return options.find((o) => o.nameShort === 'Yearly') || null;
    }
    if (currentProductName === 'Yearly') {
      return options.find((o) => o.nameShort === 'Lifetime') || null;
    }
    return options.find((o) => o.featured === 'true' && o.nameShort !== currentProductName) || null;
  }

  handleSelectOption = (option) => {
    const { onClearCoupon } = this.props;
    const { selectedOption } = this.state;
    if (selectedOption && selectedOption.id !== option.id) {
      onClearCoupon();
      this.setState({ selectedOption: option, couponCode: '' });
    } else {
      this.setState({ selectedOption: option });
    }
  };

  handleApplyCoupon = () => {
    const { onValidateCoupon } = this.props;
    const { selectedOption, couponCode } = this.state;
    if (couponCode.trim() && selectedOption) {
      this.setState({ couponApplying: true });
      onValidateCoupon(couponCode.trim(), selectedOption.id);
    }
  };

  handleRemoveCoupon = () => {
    const { onClearCoupon } = this.props;
    this.setState({ couponCode: '' });
    onClearCoupon();
  };

  handleNext = () => this.setState({ step: 'confirmation' });
  handleBack = () => this.setState({ step: 'selection' });

  handleConfirm = () => {
    const { membership, onUpgrade, couponResponse } = this.props;
    const { selectedOption, couponCode } = this.state;
    const couponValid = couponResponse && couponResponse.valid;
    this.setState({ step: 'processing' });
    onUpgrade(membership.id, selectedOption.id, couponValid ? couponCode.trim() : undefined);
  };

  handleDone = () => {
    const { onBack, onClearCoupon, onClearUpgradeResult } = this.props;
    this.setState({ step: 'selection', couponCode: '', couponApplying: false });
    onClearCoupon();
    onClearUpgradeResult();
    onBack();
  };

  render() {
    const { membershipOptions, membership, couponResponse, upgradeResult, upgradeError, onBack, onClearCoupon, onClearUpgradeResult } = this.props;
    const { step, selectedOption, couponCode, couponApplying } = this.state;

    const currentProductName = membership && membership.productName;
    const options = membershipOptions || [];

    const couponValid = couponResponse && couponResponse.valid;
    const couponInvalid = couponResponse && !couponResponse.valid;
    const showCoupon = selectedOption && selectedOption.nameShort !== 'Lifetime';

    const handleMaybeLater = () => {
      onClearCoupon();
      onClearUpgradeResult();
      onBack();
    };

    return (
      <div className="manage-upgrade">
        <button
          type="button"
          className="manage-sub-back-link mb-3"
          onClick={onBack}
          aria-label="Back"
        >
          <em className="fas fa-arrow-left" aria-hidden />
        </button>

        {/* ── STEP 1: plan selection ── */}
        {step === 'selection' && (
          <>
            <h3 className="mb-1">Upgrade Your Plan</h3>
            <p className="text-muted small mb-4">
              Get the most out of Macrostax. Unused time on your current plan is credited automatically.
            </p>

            <div className="mb-3">
              {options.map((option) => {
                const isCurrent = option.nameShort === currentProductName;
                const isMonthly = option.nameShort === 'Monthly';
                const isDisabled = isCurrent || isMonthly;
                const isSelected = selectedOption && selectedOption.id === option.id;
                return (
                  <div
                    key={option.id}
                    className={`subscription-container signup${option.featured === 'true' ? ' featured' : ''}${isSelected ? ' selected' : ''}`}
                    onClick={() => !isDisabled && this.handleSelectOption(option)}
                    style={isDisabled ? { opacity: 0.4, cursor: 'default' } : { cursor: 'pointer' }}
                  >
                    {option.featured === 'true' && option.callout && !isCurrent && (
                      <div className="featured-callout">{option.callout}</div>
                    )}
                    <div className="ml-2 mr-2">
                      <div className="d-flex flex-row align-items-baseline">
                        <h3 className="mb-0">
                          {option.nameShort} — {numeral(option.fullPrice).format('$0.00')}
                        </h3>
                        <span className="interval ml-1">{billingFrequencyLabel(option.billingFrequency)}</span>
                        {isCurrent && (
                          <span className="ml-auto small text-muted" style={{ whiteSpace: 'nowrap' }}>Current plan</span>
                        )}
                      </div>
                      {option.tagline && <div className="tagline">{option.tagline}</div>}
                      {option.secondaryCallout && option.secondaryCallout.length > 0 && (
                        <div className="secondaryCallout">{option.secondaryCallout}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {showCoupon && (
              <div className="mb-4">
                <label className="small mb-1">Have a coupon? <span className="text-muted">(optional)</span></label>
                <div className="input-group">
                  <input
                    className="form-control"
                    value={couponCode}
                    onChange={(e) => this.setState({ couponCode: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && !couponValid && this.handleApplyCoupon()}
                    placeholder="Enter coupon code"
                    disabled={couponValid}
                  />
                  <div className="input-group-append">
                    {couponValid ? (
                      <button
                        className="btn"
                        type="button"
                        style={{ border: '1px solid #ced4da', color: '#666', background: '#f8f9fa' }}
                        onClick={this.handleRemoveCoupon}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={this.handleApplyCoupon}
                        disabled={!couponCode.trim() || couponApplying}
                      >
                        {couponApplying ? 'Applying…' : 'Apply'}
                      </button>
                    )}
                  </div>
                </div>
                {couponValid && (
                  <div className="text-success small mt-1">
                    <em className="fas fa-check-circle mr-1" />
                    Coupon applied! Your new price will be ${couponResponse.finalCost} for one billing period.
                  </div>
                )}
                {couponInvalid && (
                  <div className="text-danger small mt-1">Invalid coupon code. Please try again.</div>
                )}
              </div>
            )}

            <Button color="primary" size="lg" block onClick={this.handleNext} disabled={!selectedOption}>
              {selectedOption ? `Select ${selectedOption.nameShort}` : 'Select a Plan'}
            </Button>
            <Button outline color="primary" size="lg" block className="mt-2" type="button" onClick={handleMaybeLater}>
              Maybe Later
            </Button>
          </>
        )}

        {/* ── STEP 2: confirmation ── */}
        {step === 'confirmation' && (
          <>
            <h3 className="mb-3">Confirm Upgrade</h3>
            {upgradeError && <Alert color="danger">{upgradeError}</Alert>}
            <p className="text-muted small mb-3">
              Congrats on making a commitment to yourself! You're about to upgrade to:
            </p>

            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-baseline">
                <strong>{selectedOption && selectedOption.nameShort} membership</strong>
                <span>
                  {selectedOption && numeral(selectedOption.fullPrice).format('$0.00')}
                  {selectedOption && billingFrequencyLabel(selectedOption.billingFrequency)}
                </span>
              </div>
              {couponValid && (
                <div className="text-success small mt-2">
                  <em className="fas fa-tag mr-1" />
                  Coupon ({couponResponse.name || couponCode}) — your new price will be ${couponResponse.finalCost}
                </div>
              )}
            </div>

            <p className="text-muted small mb-4">
              We will apply a credit towards this purchase for the unused time on your previous membership.
            </p>

            <Button color="primary" size="lg" block onClick={this.handleConfirm}>
              Confirm Change
            </Button>
            <Button outline color="primary" size="lg" block className="mt-2" type="button" onClick={this.handleBack}>
              Go Back
            </Button>
          </>
        )}

        {/* ── STEP 3: processing ── */}
        {step === 'processing' && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Loading…</span>
            </div>
            <p className="text-muted small mb-0">Processing your upgrade…</p>
          </div>
        )}

        {/* ── STEP 4: success ── */}
        {step === 'success' && upgradeResult && (
          <>
            <div className="text-center mb-4">
              <em className="fas fa-check-circle fa-3x text-success mb-3 d-block" />
              <h3 className="mb-1">Congrats!</h3>
              <p className="text-muted small mb-0">
                You successfully upgraded to the {selectedOption && selectedOption.nameShort} membership.
                We're so glad you're here to stay!
              </p>
            </div>

            <div className="border rounded p-3 mb-4">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted small">{selectedOption && selectedOption.nameShort} plan</span>
                <span className="small">${upgradeResult.productPrice}</span>
              </div>
              {upgradeResult.couponCredit && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">
                    Coupon{upgradeResult.couponName ? ` (${upgradeResult.couponName})` : ''}
                  </span>
                  <span className="small text-success">-${upgradeResult.couponCredit}</span>
                </div>
              )}
              {upgradeResult.prorateCredit && (
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted small">Credit applied</span>
                  <span className="small text-success">-${upgradeResult.prorateCredit}</span>
                </div>
              )}
              <hr className="my-2" />
              <div className="d-flex justify-content-between">
                <strong className="small">Total charged today</strong>
                <strong className="small">${upgradeResult.totalCost}</strong>
              </div>
            </div>

            <Button color="primary" size="lg" block onClick={this.handleDone}>
              Done
            </Button>
          </>
        )}

      </div>
    );
  }
}

export default Upgrade;
